<template>
    <div>
        <Loader :loader="isPageLoading" />
        <div v-show="!isPageLoading">
            <b-form @submit.prevent="onSubmit" @reset.prevent="onReset" novalidate>
                <b-row>
                    <b-col sm="6">
                        <Title
                            :label="`${$t('form.label.name')}*`"
                            :state="chkState('title')"
                            :value.sync="$v.form.title.$model"
                            :disabled="!isAllowedToCreateOrEdit"
                            :placeholder="$t('form.placeholder.whatDoYouWantToAchieve')"
                        >
                            <span v-if="!$v.form.title.required">
                                {{ $t('form.validations.required') }}
                            </span>
                            <span v-else-if="!$v.form.title.maxLength">
                                {{
                                    $t('form.validations.maxLength', {
                                        value: '255',
                                    })
                                }}
                            </span>
                        </Title>
                    </b-col>
                    <b-col sm="6">
                        <ActivePeriod
                            :state="chkState('period')"
                            :value.sync="$v.form.period.$model"
                            :disabled="!isAllowedToCreateOrEdit"
                            @ready="activePeriodReady = $event"
                            :label="`${$t('form.label.period')}*`"
                        >
                            <span v-if="!$v.form.period.required">
                                {{ $t('form.validations.required') }}
                            </span>
                        </ActivePeriod>
                    </b-col>
                </b-row>

                <b-row>
                    <b-col sm="12">
                        <Tags
                            :state="chkState('tags')"
                            :value.sync="$v.form.tags.$model"
                            :disabled="!isAllowedToCreateOrEdit"
                            @ready="tagsReady = $event"
                        />
                    </b-col>
                </b-row>

                <div class="mb-3">
                    <b-row>
                        <b-col sm="12">
                            <Description
                                :state="chkState('description')"
                                :value.sync="$v.form.description.$model"
                                :disabled="!isAllowedToCreateOrEdit"
                                :label="$t('form.label.description')"
                                :placeholder="$t('form.placeholder.description')"
                            />
                        </b-col>
                    </b-row>
                </div>

                <ButtonLoader
                    class="float-right ml-1"
                    type="submit"
                    variant="primary"
                    :disabled="$v.form.$invalid || btnLoader || !isAllowedToCreateOrEdit"
                    :loader="btnLoader"
                >
                    {{ $t('form.button.save') }}
                </ButtonLoader>
                <b-button class="float-right" variant="outline-primary" :disabled="btnLoader" @click="cancel">
                    {{ $t('form.button.cancel') }}
                </b-button>
            </b-form>
        </div>
    </div>
</template>

<script>
import { ActivePeriod, Description, Tags, Sections, Title } from '@/views/components/fields'
import { Select, mapperToOption, mapperToOptions } from '@/views/components/selects'
import { validationMixin } from 'vuelidate'
import { required, maxLength } from 'vuelidate/lib/validators'
import Loader from '@/views/components/loader'
import ButtonLoader from '@/views/components/button-loader'
import { mapperPdiToForm } from '../mappers/mapperPdiToForm'

const initialState = {
    title: '',
    period: {},
    description: '',
    tags: [],
}

export default {
    name: 'Form',
    components: {
        ButtonLoader,
        Select,
        Loader,
        ActivePeriod,
        Description,
        Tags,
        Sections,
        Title,
    },
    props: ['pdi', 'pdiPromise', 'isEditing', 'canEdit', 'canCreate', 'currentEventPermissions', 'moduleForm'],
    mixins: [validationMixin],
    data() {
        return {
            form: Object.assign({}, initialState),
            btnLoader: false,
            tagsReady: false,
            activePeriodReady: false,
            defaultOwnerId: null,
        }
    },
    validations: {
        form: {
            title: {
                required,
                maxLength: maxLength(255),
            },
            period: {
                required,
            },
            description: {},
            tags: {
                $each: {
                    value: {
                        required,
                    },
                    label: {
                        required,
                    },
                },
            },
        },
    },
    computed: {
        isValid() {
            return !this.$v.form.$anyError
        },
        isDirty() {
            return this.$v.form.$anyDirty
        },
        isPageLoading() {
            return !(this.tagsReady && this.activePeriodReady)
        },
        isAllowedToCreateOrEdit() {
            if (this.isEditing) {
                return this.canEdit
            }
            return this.canCreate
        },
        /*   canAssign() {
            return this.form.level.value
                ? this.currentEventPermissions[this.form.level.value].assign
                : false
        }, */
    },
    methods: {
        onSubmit() {
            if (this.validate()) {
                this.$nextTick(() => {
                    this.$emit('onSubmit', this.form, this.onReset, this.isLoading)
                })
            }
        },
        checkArrayState(current, key) {
            return current[key].$dirty ? !current[key].$invalid : null
        },
        isLoading(state) {
            this.btnLoader = state
        },
        onReset() {
            this.form = Object.assign({}, initialState)
            this.$nextTick(() => {
                this.$v.$reset()
            })
        },
        chkState(val) {
            const field = this.$v.form[val]
            return field.$dirty ? !field.$invalid : null
        },
        validate() {
            this.$v.$touch()
            return this.isValid
        },
        cancel() {
            this.$emit('cancel')
        },
    },
    watch: {
        isPageLoading() {
            return (
                this.pdiPromise &&
                this.pdiPromise.then(() => {
                    const newFormInitialState = mapperPdiToForm(this.pdi[0], mapperToOption, mapperToOptions)
                    this.form = Object.assign({}, newFormInitialState)
                })
            )
        },
    },
}
</script>

<style scoped>
.btn.disabled {
    cursor: auto;
}
</style>
